import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../lib/AuthContext";
import { useSettings } from "../lib/SettingsContext";
import { db } from "../lib/firebase";
import { addDoc, collection, getDocs, query, serverTimestamp, where } from "firebase/firestore";
import { WASH_TYPES, TIME_SLOTS, WINDOWS, windowForSlot, buildSlotAvailability, MAX_BOOKING_DAYS } from "../lib/constants";
import { useCatalog } from "../lib/CatalogContext";
import { toast } from "sonner";
import { Car, Check, ArrowLeft, ArrowRight, Plus, Download } from "lucide-react";
import { Spinner, PageLoader } from "../components/Spinner";

export default function Book() {
  const { user, profile, loading: authLoading, updateName } = useAuth();
  const { bookingEnabled } = useSettings();
  const { vehicleCategories, individualRates } = useCatalog();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [carId, setCarId] = useState(null);
  const [cars, setCars] = useState([]);
  const [washType, setWashType] = useState(null);
  const [tier, setTier] = useState(null);
  const [bikeWaxing, setBikeWaxing] = useState(false);
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeSub, setActiveSub] = useState(null);
  const [useSubscription, setUseSubscription] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [bookedAt, setBookedAt] = useState(null);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", registration: "", category: vehicleCategories[1]?.id || vehicleCategories[0]?.id || "" });
  const [addingSave, setAddingSave] = useState(false);
  const [receiptNum] = useState(() => `BW${Date.now().toString().slice(-6)}`);
  const receiptRef = useRef(null);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const needsName = !authLoading && !initLoading && !!user && !profile?.name?.trim();

  const handleSaveName = async () => {
    if (!nameInput.trim()) { toast.error("Name is required"); return; }
    setSavingName(true);
    try {
      await updateName(nameInput.trim());
      toast.success("Thanks!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingName(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { state: { from: "/book" } });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setInitLoading(false); return; }
    (async () => {
      try {
        const carsQ = query(collection(db, "cars"), where("uid", "==", user.uid));
        const carsSnap = await getDocs(carsQ);
        setCars(carsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        const subQ = query(
          collection(db, "subscriptions"),
          where("uid", "==", user.uid),
          where("status", "==", "active")
        );
        const subSnap = await getDocs(subQ);
        if (!subSnap.empty) {
          const d = subSnap.docs[0];
          setActiveSub({ id: d.id, ...d.data() });
        }
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user, authLoading]);

  const car = cars.find((c) => c.id === carId);
  const isBike = car?.category === "bike";
  const isHatchback = car?.category !== "fullsize_suv";
  const rateId = `${tier}_${isHatchback ? "hatchback" : "suv"}`;
  const rate = individualRates.find((r) => r.id === rateId);
  const bikeRate = individualRates.find((r) => r.id === "bike_wash");
  const bikePrice = (bikeRate?.washPolish || 0) + (bikeWaxing ? (bikeRate?.waxing || 0) : 0);
  const cashPrice = isBike ? bikePrice : (washType && rate ? rate[washType] : 0);
  const subWashesLeft = activeSub?.washesRemaining || 0;
  const canUseSub = !!activeSub && subWashesLeft > 0 && !isBike;
  const price = useSubscription && canUseSub ? 0 : cashPrice;

  useEffect(() => {
    if (!date) return;
    const dateStr = date.toISOString().split("T")[0];
    const fetchSlots = async () => {
      setSlotsLoading(true);
      try {
        const q = query(
          collection(db, "bookings"),
          where("date", "==", dateStr),
          where("status", "in", ["pending", "confirmed"])
        );
        const snap = await getDocs(q);
        setBookedSlots(snap.docs.map((d) => d.data().timeSlot));
      } finally {
        setSlotsLoading(false);
      }
    };
    fetchSlots();
  }, [date]);

  useEffect(() => {
    if (step !== 4 || !user) return;
    (async () => {
      const subQ = query(
        collection(db, "subscriptions"),
        where("uid", "==", user.uid),
        where("status", "==", "active")
      );
      const snap = await getDocs(subQ);
      if (!snap.empty) {
        const d = snap.docs[0];
        setActiveSub({ id: d.id, ...d.data() });
      } else {
        setActiveSub(null);
        setUseSubscription(false);
      }
    })();
  }, [step, user]);

  const handleAddVehicle = async () => {
    if (!addForm.name.trim()) { toast.error("Vehicle name is required"); return; }
    setAddingSave(true);
    try {
      await addDoc(collection(db, "cars"), {
        uid: user.uid,
        name: addForm.name.trim(),
        registration: addForm.registration.trim().toUpperCase(),
        category: addForm.category,
        createdAt: serverTimestamp(),
      });
      toast.success("Vehicle added");
      setAddForm({ name: "", registration: "", category: vehicleCategories[1]?.id || vehicleCategories[0]?.id || "" });
      setShowAddVehicle(false);
      const carsQ = query(collection(db, "cars"), where("uid", "==", user.uid));
      const carsSnap = await getDocs(carsQ);
      setCars(carsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) { toast.error(err.message); }
    finally { setAddingSave(false); }
  };

  const handleConfirm = async () => {
    if (!car || (!isBike && (!tier || !washType)) || !date || !time) return;
    if (!profile?.name?.trim()) { toast.error("Please add your name before booking"); return; }
    setSubmitting(true);
    try {
      const carQ = query(
        collection(db, "bookings"),
        where("carId", "==", car.id),
        where("status", "in", ["pending", "confirmed"])
      );
      const carSnap = await getDocs(carQ);
      if (!carSnap.empty) {
        toast.error("This vehicle already has an upcoming wash. Complete or cancel it first.");
        setSubmitting(false);
        return;
      }
      const dateStr = date.toISOString().split("T")[0];
      const washTypeLabel = WASH_TYPES.find((w) => w.id === washType)?.label || washType;
      await addDoc(collection(db, "bookings"), {
        uid: user.uid,
        userPhone: user?.phoneNumber || profile?.phone || "",
        userName: profile?.name || user?.phoneNumber || "Customer",
        carId: car.id,
        carName: car.name || "",
        carRegistration: car.registration || "",
        vehicleType: car.category,
        vehicleLabel: vehicleCategories.find((c) => c.id === car.category)?.short || car.category || "",
        serviceType: isBike ? "bike_wash" : rateId,
        serviceLabel: isBike ? washTierLabel : `${tier === "premium" ? "Premium" : "Basic"} · ${washTypeLabel}`,
        washType: isBike ? "washPolish" : washType,
        tier: isBike ? null : tier,
        bikeWaxing: isBike ? bikeWaxing : null,
        date: dateStr,
        timeSlot: time,
        price,
        useSubscription: useSubscription && canUseSub,
        subscriptionId: useSubscription && canUseSub ? activeSub.id : null,
        status: "pending",
        paymentMode: useSubscription && canUseSub ? "subscription" : "cash",
        createdAt: serverTimestamp(),
      });
      setBookedAt(new Date());
      setConfirmed(true);
    } catch (err) {
      toast.error("Failed to book: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadReceipt = async () => {
    if (!receiptRef.current) return;
    try {
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(receiptRef.current, { pixelRatio: 2, backgroundColor: "#ffffff" });

      // Web Share API — saves to gallery on iOS/Android
      try {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const file = new File([blob], `BW-${receiptNum}.png`, { type: "image/png" });
        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: `Baywash Receipt #${receiptNum}` });
          return;
        }
      } catch {}

      // Fallback: direct download
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `BW-${receiptNum}.png`;
      a.click();
    } catch {
      toast.error("Could not save receipt");
    }
  };

  const canNext =
    (step === 1 && carId) ||
    (step === 2 && (isBike || (tier && washType))) ||
    (step === 3 && date && time);

  const vehicleCat = vehicleCategories.find((c) => c.id === car?.category);
  const washLabel = WASH_TYPES.find((w) => w.id === washType)?.label || "";
  const washTierLabel = isBike
    ? `Wash & Polish${bikeWaxing ? " + Waxing" : ""}`
    : `${tier === "premium" ? "Premium" : "Basic"} · ${washLabel}`;
  const washDuration = isBike ? "20 min" : (washType === "interiorExterior" ? "40 min" : washType === "exteriorOnly" ? "20 min" : null);
  const dateLabel = date?.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  if (!authLoading && !initLoading && !bookingEnabled) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-32 pb-24 px-6">
          <div className="max-w-2xl mx-auto text-center py-24" data-testid="booking-coming-soon">
            <div className="font-mono-accent text-xs text-[#002FA7] mb-6">[ BOOKING ]</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-6">Coming Soon.</h1>
            <p className="text-[#525252] text-lg">
              Online booking is temporarily paused. Please check back shortly.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-32 pb-24 px-6">
        {initLoading ? <PageLoader label="Loading your account…" /> : (
        <div className="max-w-5xl mx-auto">

          {!confirmed && (
            <>
              <div className="font-mono-accent text-xs text-[#002FA7] mb-3">[ BOOKING WIZARD ]</div>
              <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-6">Book your wash.</h1>
            </>
          )}

          {activeSub && !confirmed && (
            <div className="border-2 border-[#00D05A] bg-[#E8FAF0] p-4 mb-8 flex items-center justify-between">
              <div>
                <div className="font-mono-accent text-[10px] text-[#002FA7]">ACTIVE PACKAGE</div>
                <div className="font-display font-bold text-lg">{activeSub.planName}</div>
                <div className="text-xs text-[#525252]">{subWashesLeft} wash{subWashesLeft !== 1 ? "es" : ""} remaining</div>
              </div>
              <div className="font-display font-black text-3xl text-[#00D05A]">{subWashesLeft}</div>
            </div>
          )}

          {/* STEPS BAR */}
          {!confirmed && (
            <div className="flex gap-2 mb-12">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className={`flex-1 h-1 ${s <= step ? "bg-[#002FA7]" : "bg-[#D4D4D8]"}`} />
              ))}
            </div>
          )}

          {/* STEP 1: VEHICLE */}
          {step === 1 && !confirmed && (
            <div data-testid="step-vehicle" className="animate-fade-up">
              <div className="font-mono-accent text-xs text-[#525252] mb-3">STEP 01 / 04</div>
              <h2 className="font-display font-bold text-2xl mb-8">Which vehicle?</h2>

              {cars.length === 0 && (
                <div className="border border-dashed border-[#D4D4D8] p-10 text-center mb-6">
                  <Car size={32} className="mx-auto text-[#525252] mb-3" strokeWidth={1.5} />
                  <div className="font-display font-bold text-xl mb-2">No vehicles on your profile</div>
                  <p className="text-sm text-[#525252] mb-6">Add a vehicle first — it takes 10 seconds.</p>
                  <Button
                    onClick={() => setShowAddVehicle(true)}
                    className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wider text-xs h-12 px-6"
                  >
                    <Plus size={16} className="mr-2" /> Add a Vehicle
                  </Button>
                </div>
              )}

              {cars.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-px bg-[#D4D4D8]">
                  {cars.map((c) => {
                    const cat = vehicleCategories.find((x) => x.id === c.category);
                    const active = carId === c.id;
                    return (
                      <button
                        key={c.id}
                        data-testid={`car-${c.id}`}
                        onClick={() => setCarId(c.id)}
                        className={`bg-white p-6 text-left transition-colors hover:bg-[#F4F4F5] ${active ? "ring-2 ring-inset ring-[#002FA7] bg-[#F4F4F5]" : ""}`}
                      >
                        <Car size={28} strokeWidth={1.5} className="mb-4" />
                        <div className="font-display font-bold text-xl mb-1">{c.name}</div>
                        <div className="font-mono text-xs text-[#525252]">{c.registration || "no reg"}</div>
                        <div className="font-mono-accent text-[10px] text-[#002FA7] mt-2">
                          {cat?.short || c.category} · {cat?.id === "bike" ? "Bike pricing" : cat?.id === "fullsize_suv" ? "SUV pricing" : "Hatchback pricing"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {cars.length > 0 && (
                <button onClick={() => setShowAddVehicle(true)} className="inline-block mt-6 font-mono-accent text-xs text-[#002FA7] hover:underline">
                  + ADD ANOTHER VEHICLE
                </button>
              )}
            </div>
          )}

          {/* STEP 2: WASH TYPE */}
          {step === 2 && !confirmed && (
            <div data-testid="step-service" className="animate-fade-up">
              <div className="font-mono-accent text-xs text-[#525252] mb-3">STEP 02 / 04</div>
              <h2 className="font-display font-bold text-2xl mb-4">Choose your wash</h2>
              <p className="text-sm text-[#525252] mb-8">
                Prices for <span className="font-bold">{car?.name}</span> ({isBike ? "Bike" : isHatchback ? "Hatchback" : "SUV"} tier).
              </p>

              {isBike ? (
                <div className="max-w-md">
                  <div className="border-2 border-[#002FA7] bg-[#F4F4F5] p-8 mb-4">
                    <div className="font-mono-accent text-xs text-[#002FA7] mb-3">BIKE WASH</div>
                    <div className="font-display font-bold text-2xl mb-4">{bikeRate?.label || "Wash & Polish"}</div>
                    <div className="font-display font-black text-3xl text-[#002FA7] mb-3">₹{bikeRate?.washPolish ?? 0}</div>
                    <div className="font-mono-accent text-[10px] text-[#525252]">⏱ 20 min</div>
                  </div>
                  <button
                    type="button"
                    data-testid="bike-waxing-toggle"
                    onClick={() => setBikeWaxing((v) => !v)}
                    className={`w-full flex items-center justify-between p-6 border-2 transition-colors ${
                      bikeWaxing ? "border-[#002FA7] bg-[#F4F4F5]" : "border-[#D4D4D8] hover:bg-[#F4F4F5]"
                    }`}
                  >
                    <div className="text-left">
                      <div className="font-bold">+ Add Waxing</div>
                      <div className="text-xs text-[#525252]">Extra shine & protection</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-display font-black text-xl text-[#002FA7]">+₹{bikeRate?.waxing ?? 0}</span>
                      <span className={`w-5 h-5 border-2 flex items-center justify-center shrink-0 ${bikeWaxing ? "bg-[#002FA7] border-[#002FA7]" : "border-[#D4D4D8]"}`}>
                        {bikeWaxing && <Check size={14} className="text-white" />}
                      </span>
                    </div>
                  </button>
                </div>
              ) : (
                <>
                  <div className="font-mono-accent text-xs text-[#525252] mb-3">STEP A · SELECT A TIER</div>
                  <div className="inline-flex border border-black mb-8">
                    {["basic", "premium"].map((t) => (
                      <button
                        key={t}
                        data-testid={`tier-${t}`}
                        onClick={() => { setTier(t); setWashType(null); }}
                        className={`px-6 py-3 font-mono-accent text-xs uppercase ${tier === t ? "bg-black text-white" : "bg-white"}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>

                  {!tier ? (
                    <div className="border border-dashed border-[#D4D4D8] p-10 text-center">
                      <p className="text-sm text-[#525252]">Choose Basic or Premium above to see wash prices.</p>
                    </div>
                  ) : (
                    <>
                      <div className="font-mono-accent text-xs text-[#525252] mb-3">STEP B · SELECT A WASH TYPE</div>
                      <div className="grid md:grid-cols-2 gap-px bg-[#D4D4D8]">
                        {WASH_TYPES.map((wt) => {
                          const active = washType === wt.id;
                          const wtPrice = rate?.[wt.id];
                          return (
                            <button
                              key={wt.id}
                              data-testid={`washtype-${wt.id}`}
                              onClick={() => setWashType(wt.id)}
                              className={`bg-white p-8 text-left transition-colors hover:bg-[#F4F4F5] ${active ? "ring-2 ring-inset ring-[#002FA7] bg-[#F4F4F5]" : ""}`}
                            >
                              <div className="font-mono-accent text-xs text-[#002FA7] mb-3">{wt.short}</div>
                              <div className="font-display font-bold text-2xl mb-4">{wt.label}</div>
                              <div className="font-display font-black text-3xl text-[#002FA7] mb-3">₹{wtPrice}</div>
                              <div className="font-mono-accent text-[10px] text-[#525252]">
                                {wt.id === "interiorExterior" ? "⏱ 40 min" : "⏱ 20 min"}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* STEP 3: DATE & TIME */}
          {step === 3 && !confirmed && (
            <div data-testid="step-datetime" className="animate-fade-up">
              <div className="font-mono-accent text-xs text-[#525252] mb-3">STEP 03 / 04</div>
              <h2 className="font-display font-bold text-2xl mb-8">When?</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="border border-[#D4D4D8] p-4">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { setDate(d); setTime(null); }}
                    disabled={(d) => {
                      const today = new Date(new Date().setHours(0, 0, 0, 0));
                      const maxDate = new Date(today);
                      maxDate.setDate(maxDate.getDate() + MAX_BOOKING_DAYS - 1);
                      return d < today || d > maxDate;
                    }}
                    className="rounded-none"
                    data-testid="booking-calendar"
                  />
                </div>
                <div>
                  <div className="font-mono-accent text-xs text-[#525252] mb-4">
                    AVAILABLE SLOTS · {washType === "interiorExterior" ? "40 MIN TOTAL" : "20 MIN"}
                  </div>
                  {!date && <div className="text-sm text-[#525252]">Pick a date first.</div>}
                  {date && slotsLoading && (
                    <div className="flex items-center gap-2 text-sm text-[#525252]">
                      <Spinner /> Checking availability…
                    </div>
                  )}
                  {date && !slotsLoading && (() => {
                    const { map } = buildSlotAvailability(bookedSlots, date);
                    const hasAnySlot = TIME_SLOTS.some((s) => map[s]?.ok);
                    return (
                      <div className="space-y-6">
                        {!hasAnySlot && (
                          <div className="border border-[#FFC000] bg-[#FFFCE6] p-4 font-mono-accent text-xs text-[#B58900]">
                            NO SLOTS AVAILABLE FOR THIS DATE. PLEASE SELECT ANOTHER DATE.
                          </div>
                        )}
                        {WINDOWS.map((w) => {
                          const wSlots = TIME_SLOTS
                            .filter((s) => windowForSlot(s) === w.key)
                            .filter((s) => !map[s].past);
                          if (wSlots.length === 0) return null;
                          return (
                            <div key={w.key}>
                              <div className="font-mono-accent text-[10px] text-[#525252] mb-2">{w.label}</div>
                              <div className="grid grid-cols-3 gap-px bg-[#D4D4D8] border border-[#D4D4D8]">
                                {wSlots.map((t) => {
                                  const av = map[t];
                                  const active = time === t;
                                  return (
                                    <button
                                      key={t}
                                      data-testid={`time-slot-${t}`}
                                      disabled={!av.ok}
                                      onClick={() => setTime(t)}
                                      className={`py-3 font-mono text-sm transition-colors ${
                                        active ? "bg-[#002FA7] text-white"
                                        : !av.ok ? "bg-white text-[#D4D4D8] cursor-not-allowed line-through"
                                        : "bg-white text-[#0A0A0A] hover:bg-[#F4F4F5]"
                                      }`}
                                    >
                                      {t}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                        <div className="font-mono-accent text-[10px] text-[#525252] pt-2 border-t border-[#D4D4D8]">
                          ● 1:00 PM — 2:00 PM IS LUNCH (NO SLOTS)
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: REVIEW (before confirm) */}
          {step === 4 && !confirmed && (
            <div data-testid="step-confirm" className="animate-fade-up max-w-2xl">
              <div className="font-mono-accent text-xs text-[#525252] mb-3">STEP 04 / 04</div>
              <h2 className="font-display font-bold text-2xl mb-8">Review your booking</h2>

              <div className="border border-[#D4D4D8] divide-y divide-[#D4D4D8] mb-8">
                <InfoRow label="Customer" value={profile?.name || user?.phoneNumber} />
                <InfoRow label="Vehicle" value={`${car?.name}${car?.registration ? ` · ${car.registration}` : ""}`} />
                <InfoRow label="Category" value={vehicleCat?.short || car?.category} />
                <InfoRow label="Wash" value={washTierLabel} />
                <InfoRow label="Duration" value={washDuration} />
                <InfoRow label="Date" value={dateLabel} />
                <InfoRow label="Time" value={time} mono />
              </div>

              {activeSub && (
                <div className={`border-2 ${useSubscription && canUseSub ? "border-[#00D05A] bg-[#E8FAF0]" : "border-[#D4D4D8]"} p-4 mb-8`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="font-mono-accent text-[10px] text-[#002FA7] mb-1">YOUR PACKAGE · {activeSub.planName}</div>
                      <div className="text-sm">
                        {canUseSub
                          ? <><span className="font-bold">{subWashesLeft}</span> wash{subWashesLeft > 1 ? "es" : ""} remaining</>
                          : <span className="text-[#FF2A2A] font-bold">0 washes left — package exhausted</span>}
                      </div>
                    </div>
                    <button
                      data-testid="use-subscription-toggle"
                      type="button"
                      onClick={() => setUseSubscription(!useSubscription)}
                      disabled={!canUseSub}
                      className={`shrink-0 px-4 py-2 font-mono-accent text-[10px] uppercase border-2 transition-colors ${
                        !canUseSub ? "border-[#D4D4D8] text-[#D4D4D8] cursor-not-allowed"
                        : useSubscription ? "bg-[#00D05A] border-[#00D05A] text-black"
                        : "border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                      }`}
                    >
                      {useSubscription && canUseSub ? "✓ USING 1 WASH" : "USE PACKAGE"}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-baseline justify-between border-t-2 border-[#0A0A0A] pt-6">
                <div className="font-mono-accent text-xs text-[#525252]">
                  {useSubscription && canUseSub ? "COVERED BY PACKAGE" : "TOTAL DUE ON SERVICE"}
                </div>
                <div className="font-display font-black text-4xl text-[#002FA7]">
                  {useSubscription && canUseSub ? "FREE" : `₹${price}`}
                </div>
              </div>
            </div>
          )}

          {/* CONFIRMED: receipt + download */}
          {confirmed && (
            <div className="animate-fade-up max-w-lg mx-auto">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-10 bg-[#00D05A] flex items-center justify-center shrink-0">
                  <Check size={20} className="text-black" />
                </div>
                <div>
                  <div className="font-display font-bold text-2xl">Booking Confirmed!</div>
                  <div className="text-sm text-[#525252]">We'll see you soon. Save your receipt below.</div>
                </div>
              </div>

              {/* Receipt — captured by html-to-image */}
              <div
                ref={receiptRef}
                className="bg-white border-2 border-dashed border-[#0A0A0A] p-8"
                style={{ fontFamily: "monospace" }}
              >
                <div className="flex justify-between items-start mb-6 pb-6 border-b border-[#D4D4D8]">
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 20, letterSpacing: "-0.05em", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ display: "inline-block", width: 18, height: 18, background: "#002FA7" }} />
                      BAYWASH
                    </div>
                    <div style={{ fontSize: 10, color: "#525252", marginTop: 4, letterSpacing: "0.1em" }}>BOOKING RECEIPT</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, color: "#525252", letterSpacing: "0.1em" }}>RECEIPT NO.</div>
                    <div style={{ fontWeight: 900, fontSize: 22, marginTop: 4 }}>#{receiptNum}</div>
                  </div>
                </div>

                <div style={{ marginBottom: 24, borderBottom: "1px solid #D4D4D8", paddingBottom: 24 }}>
                  {[
                    { label: "Customer", value: profile?.name || user?.phoneNumber },
                    { label: "Vehicle", value: `${car?.name}${car?.registration ? ` · ${car.registration}` : ""}` },
                    { label: "Category", value: vehicleCat?.short || car?.category },
                    { label: "Wash", value: washTierLabel },
                    { label: "Duration", value: washDuration },
                    { label: "Appt. Date", value: dateLabel },
                    { label: "Appt. Time", value: time },
                    { label: "Subscription", value: useSubscription && canUseSub ? activeSub?.planName || "Package" : "Not used" },
                    { label: "Payment", value: useSubscription && canUseSub ? "Package" : "Cash" },
                    { label: "Booked on", value: bookedAt ? bookedAt.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }) : "" },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: 13 }}>
                      <span style={{ color: "#525252", letterSpacing: "0.05em" }}>{label.toUpperCase()}</span>
                      <span style={{ fontWeight: 700, textAlign: "right", maxWidth: "55%" }}>{value}</span>
                    </div>
                  ))}
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 10, color: "#525252", letterSpacing: "0.1em" }}>TOTAL</span>
                  <span style={{ fontWeight: 900, fontSize: 32, color: "#002FA7" }}>
                    {useSubscription && canUseSub ? "FREE" : `₹${price}`}
                  </span>
                </div>

                <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #D4D4D8", textAlign: "center", fontSize: 10, color: "#525252", letterSpacing: "0.05em" }}>
                  <div>THE EXPRESS CAR WASH · ON DEMAND · ZERO WAIT</div>
                  <div style={{ marginTop: 4 }}>info@baywashindia.com · +91 7007 206 812</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 mt-8">
                <Button
                  onClick={downloadReceipt}
                  className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs px-6 h-12"
                >
                  <Download size={16} className="mr-2" /> Save Receipt
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="rounded-none border-2 border-black font-bold uppercase tracking-wide text-xs px-6 h-12"
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          )}

          {/* NAV BUTTONS */}
          {!confirmed && (
            <div className="flex justify-between mt-12">
              <Button
                data-testid="step-back-button"
                variant="ghost"
                onClick={() => setStep(Math.max(1, step - 1))}
                disabled={step === 1}
                className="rounded-none font-bold uppercase tracking-wide text-xs"
              >
                <ArrowLeft size={16} className="mr-2" /> Back
              </Button>
              {step < 4 ? (
                <Button
                  data-testid="step-next-button"
                  onClick={() => setStep(step + 1)}
                  disabled={!canNext}
                  className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs px-8 h-12"
                >
                  Continue <ArrowRight size={16} className="ml-2" />
                </Button>
              ) : (
                <Button
                  data-testid="booking-confirm-button"
                  onClick={handleConfirm}
                  disabled={submitting || !profile?.name?.trim()}
                  className="bg-[#00D05A] text-black hover:bg-[#0A0A0A] hover:text-white rounded-none font-bold uppercase tracking-wide text-xs px-8 h-12"
                >
                  {submitting ? <Spinner /> : <><Check size={16} className="mr-2" /> Confirm Booking</>}
                </Button>
              )}
            </div>
          )}

        </div>
        )}
      </main>

      {/* Add Vehicle Dialog — opens directly from booking flow */}
      <Dialog open={showAddVehicle} onOpenChange={setShowAddVehicle}>
        <DialogContent className="max-w-md rounded-none border-[#D4D4D8]">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tighter">Add Vehicle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="font-mono-accent text-xs text-[#525252] block mb-2">VEHICLE NAME</label>
              <Input
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="e.g. My Swift, Dad's SUV…"
                className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12"
              />
            </div>
            <div>
              <label className="font-mono-accent text-xs text-[#525252] block mb-2">CATEGORY</label>
              <Select value={addForm.category} onValueChange={(v) => setAddForm({ ...addForm, category: v })}>
                <SelectTrigger className="rounded-none border-[#D4D4D8]">
                  <SelectValue placeholder="Select type…" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {vehicleCategories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-mono-accent text-xs text-[#525252] block mb-2">REGISTRATION (OPTIONAL)</label>
              <Input
                value={addForm.registration}
                onChange={(e) => setAddForm({ ...addForm, registration: e.target.value.toUpperCase() })}
                placeholder="KA-01-AB-1234"
                style={{ textTransform: "uppercase" }}
                className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12 font-mono"
              />
            </div>
            <Button
              onClick={handleAddVehicle}
              disabled={addingSave}
              className="w-full bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs h-12"
            >
              {addingSave ? <Spinner /> : "Save Vehicle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mandatory name gate — a customer can't book without a name on file.
          Non-dismissible: open is driven by needsName itself, not local
          dialog state, and onOpenChange is a no-op, so Escape/overlay-click/
          the close button can't get around it. */}
      <Dialog open={needsName} onOpenChange={() => {}}>
        <DialogContent className="max-w-md rounded-none border-[#D4D4D8] [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tighter">What's your name?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#525252] -mt-2 mb-2">We need this before you can book a wash.</p>
          <div className="space-y-4">
            <Input
              data-testid="booking-name-input"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSaveName(); }}
              placeholder="Full name"
              className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12"
              autoFocus
            />
            <Button
              data-testid="booking-name-save"
              onClick={handleSaveName}
              disabled={savingName}
              className="w-full bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs h-12"
            >
              {savingName ? <Spinner /> : "Continue"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

const InfoRow = ({ label, value, mono }) => (
  <div className="flex justify-between items-baseline px-6 py-4">
    <div className="font-mono-accent text-xs text-[#525252]">{label}</div>
    <div className={`text-right font-bold ${mono ? "font-mono text-lg" : ""}`}>{value}</div>
  </div>
);
